import { useEffect, useRef, useState } from "react";
import { Check, Minus, Plus } from "lucide-react";
import { money, sizeLabel } from "@/lib/signs";

/** A panel that opens above a field on desktop, and as a bottom sheet on mobile so it never gets clipped by the horizontally-scrolling dock. */
export function Popover({ children, wide, red, testid, className = "" }) {
    const width = typeof wide === "string" ? wide : wide ? "w-[340px] max-w-[92vw]" : "min-w-[180px]";
    return (
        <div className="fixed inset-x-0 bottom-0 z-[60] p-3 lg:absolute lg:inset-x-auto lg:bottom-full lg:left-0 lg:z-50 lg:p-0 lg:pb-2.5" data-testid={testid}>
            <div className={`relative mx-auto rounded-xl border bg-white p-1.5 ring-1 ring-black/5 max-lg:w-full ${width} ${red ? "border-red-300 bg-red-50" : "border-zinc-300"} ${className}`}
                style={{ boxShadow: "0 8px 20px -6px rgba(15,23,42,0.35), 0 28px 60px -12px rgba(15,23,42,0.5)" }}>
                {children}
                <span className={`absolute -bottom-1.5 left-6 hidden h-3 w-3 rotate-45 border-b border-r bg-white lg:block ${red ? "border-red-300 bg-red-50" : "border-zinc-300"}`} />
            </div>
        </div>
    );
}

/** Closes a panel when the mouse goes down anywhere else, or on Escape. */
function useCloseOnOutside(ref, open, close) {
    useEffect(() => {
        if (!open) return;
        const down = (e) => { if (!ref.current?.contains(e.target)) close(); };
        const key = (e) => { if (e.key === "Escape") close(); };
        document.addEventListener("mousedown", down);
        document.addEventListener("keydown", key);
        return () => { document.removeEventListener("mousedown", down); document.removeEventListener("keydown", key); };
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** True only on devices with a real hovering pointer (mouse/trackpad), so touch taps don't fight hover-to-open. */
const canHover = () => typeof window !== "undefined" && window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;

/** The white card every option lives in. It only goes grey when the option is off-limits right now.
 *  `attention` paints it red with a "!" badge until the customer fills it in. */
export function FieldShell({ label, blocked, open, theme, children, note, onClick, onEnter, onLeave, testid, className = "", containerRef, attention }) {
    const [hover, setHover] = useState(false);
    const alert = attention && !blocked && !open;
    return (
        <div ref={containerRef} className="relative shrink-0" onMouseEnter={() => { setHover(true); if (canHover()) onEnter?.(); }} onMouseLeave={() => { setHover(false); if (canHover()) onLeave?.(); }}>
            <button type="button" onClick={blocked ? undefined : onClick} aria-disabled={blocked || undefined} data-testid={testid}
                data-blocked={blocked ? "true" : undefined} data-attention={alert ? "true" : undefined}
                className={`relative rounded-xl border px-3 py-2 text-left transition-[border-color,box-shadow] duration-150 ${blocked ? "cursor-not-allowed border-zinc-200 bg-zinc-100 text-zinc-400" : alert ? "border-red-500 bg-white text-zinc-900 shadow-[0_0_0_3px_rgba(239,68,68,0.15)]" : "border-zinc-200 bg-white text-zinc-900 hover:shadow-sm"} ${className}`}
                style={!blocked && !alert && (open || hover) ? { borderColor: theme?.primary } : undefined}>
                <span className={`block font-mono text-[10px] uppercase tracking-[0.18em] ${alert ? "text-red-600" : "text-zinc-500"}`}>{label}</span>
                <span className={`mt-0.5 block whitespace-nowrap text-sm font-bold leading-5 ${alert ? "text-red-600 underline decoration-red-500 decoration-2 underline-offset-2" : ""}`}>{children}</span>
                {alert && (
                    <span className="absolute -right-1.5 -top-1.5 grid h-4 w-4 animate-pulse place-items-center rounded-full bg-red-500 text-[10px] font-black leading-none text-white" data-testid={`${testid}-attention`}>!</span>
                )}
            </button>
            {blocked && hover && (
                <Popover red testid={`${testid}-blocked-note`}>
                    <p className="max-w-[240px] px-2 py-1 text-xs font-semibold text-red-700">{blocked}</p>
                </Popover>
            )}
            {note}
        </div>
    );
}

/** Two choices. Hover shows both like a switch, a click flips to the other one. */
export function ToggleField({ label, choices, value, onChange, blocked, theme, testid }) {
    const [hover, setHover] = useState(false);
    const cur = choices.find((c) => c.id === value) || choices[choices.length - 1];
    const other = choices.find((c) => c.id !== cur.id) || cur;
    const showSwitch = hover && !blocked;
    return (
        <FieldShell label={label} blocked={blocked} theme={theme} testid={testid}
            onEnter={() => setHover(true)} onLeave={() => setHover(false)} onClick={() => onChange(other.id)}>
            <span className="grid">
                <span className={`col-start-1 row-start-1 transition-opacity duration-150 ${showSwitch ? "opacity-0" : "opacity-100"}`} data-testid={`${testid}-value`}>{cur.label}</span>
                <span className={`col-start-1 row-start-1 flex items-center gap-0.5 rounded-full bg-zinc-100 p-0.5 transition-opacity duration-150 ${showSwitch ? "opacity-100" : "pointer-events-none opacity-0"}`} data-testid={`${testid}-switch`}>
                    {choices.map((c) => (
                        <span key={c.id} className="rounded-full px-2 py-0.5 text-[11px] font-bold uppercase leading-4 tracking-wide"
                            style={c.id === cur.id ? { background: theme?.primary, color: theme?.on } : { color: "#71717a" }}>{c.label}</span>
                    ))}
                </span>
            </span>
        </FieldShell>
    );
}

/** Three or more choices. Click opens the list above the field; hover also opens it on desktop. Closes on outside click. */
export function ChoiceField({ label, choices, value, onChange, blocked, theme, help, testid, render }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useCloseOnOutside(ref, open, () => setOpen(false));
    const cur = choices.find((c) => c.id === value);
    return (
        <FieldShell containerRef={ref} label={label} blocked={blocked} open={open} theme={theme} testid={testid}
            onEnter={() => !blocked && setOpen(true)} onLeave={() => setOpen(false)} onClick={() => setOpen((o) => !o)}
            note={open && !blocked && (
                <Popover testid={`${testid}-menu`}>
                    {help && <p className="max-w-[240px] px-2.5 pb-1.5 pt-1 text-[11px] leading-snug text-zinc-500">{help}</p>}
                    {choices.map((c) => (
                        <button key={c.id} type="button" disabled={c.disabled} data-testid={`${testid}-${c.id}`}
                            onClick={() => { onChange(c.id); setOpen(false); }}
                            className={`flex w-full items-center justify-between gap-4 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-left text-sm transition-colors ${c.disabled ? "cursor-not-allowed text-zinc-400" : "hover:bg-zinc-100"} ${c.id === value ? "font-bold" : "text-zinc-700"}`}
                            style={c.id === value ? { color: theme?.ink || theme?.primary } : undefined}>
                            <span>{render ? render(c) : c.label}{c.disabled && c.note ? <span className="ml-2 text-[11px] font-normal">{c.note}</span> : null}</span>
                            {c.id === value && <Check size={13} />}
                        </button>
                    ))}
                </Popover>
            )}>
            {cur ? <span data-testid={`${testid}-value`}>{render ? render(cur) : cur.label}</span> : "Pick"}
        </FieldShell>
    );
}

function Num({ label, value, onChange, min, max, theme, testid }) {
    return (
        <label className="block">
            <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</span>
            <input type="number" inputMode="decimal" value={value} min={min} max={max} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                onFocus={(e) => (e.target.style.borderColor = theme?.primary)} onBlur={(e) => (e.target.style.borderColor = "")}
                className="mt-1 w-full rounded-lg border border-zinc-300 bg-white px-2.5 py-2 font-mono text-sm text-zinc-900 outline-none" />
        </label>
    );
}

/** A field that opens a working panel on click (inputs, steppers, lists) and stays open until you click away. */
export function PanelField({ label, value, blocked, theme, testid, panelTestid, wide = true, dot, attention, children }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useCloseOnOutside(ref, open, () => setOpen(false));
    return (
        <div ref={ref} className="relative shrink-0">
            <FieldShell label={label} open={open} blocked={blocked} theme={theme} testid={testid} attention={attention} onClick={() => setOpen((o) => !o)}>
                <span data-testid={`${testid}-value`}>{value}</span>
                {dot}
            </FieldShell>
            {open && !blocked && (
                <Popover wide={wide} testid={panelTestid || `${testid}-panel`}>
                    {typeof children === "function" ? children(() => setOpen(false)) : children}
                </Popover>
            )}
        </div>
    );
}

/** A number you step up and down without opening anything. */
export function StepperField({ label, value, onChange, min = 1, max = Infinity, theme, testid, unit }) {
    const btn = "grid h-6 w-6 place-items-center rounded-full border border-zinc-300 text-zinc-700 transition-colors hover:border-zinc-500 disabled:opacity-30";
    return (
        <div className="relative shrink-0 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-left" data-testid={testid}>
            <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</span>
            <span className="mt-0.5 flex items-center gap-2 text-sm font-bold leading-5 text-zinc-900">
                <button type="button" className={btn} disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))} data-testid={`${testid}-minus`}><Minus size={11} /></button>
                <span className="min-w-[1.5rem] text-center font-mono" data-testid={`${testid}-value`}>{value}</span>
                <button type="button" className={btn} disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))} data-testid={`${testid}-plus`}><Plus size={11} /></button>
                {unit && <span className="text-xs font-semibold text-zinc-500">{unit}</span>}
            </span>
        </div>
    );
}

/** A read-only number in the same white card, with an optional meter under it. */
export function StatField({ label, value, pct, theme, testid }) {
    return (
        <div className="relative shrink-0 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-left" data-testid={testid}>
            <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</span>
            <span className="mt-0.5 block whitespace-nowrap text-sm font-bold leading-5 text-zinc-900" data-testid={`${testid}-value`}>{value}</span>
            {pct != null && (
                <span className="mt-1 block h-1 w-24 overflow-hidden rounded-full bg-zinc-200">
                    <span className="block h-full rounded-full transition-[width] duration-500" style={{ width: `${Math.min(100, pct)}%`, background: theme?.primary }} />
                </span>
            )}
        </div>
    );
}

/** Width × height × quantity, with the file's real size remembered underneath. */
export function SizeField({ product, cfg, theme }) {
    const presets = product.preset_sizes || [];
    const bySheet = product.pricing_model === "per_sheet_bulk";
    const qtyLabel = bySheet ? "Pieces" : product.pricing_model === "per_bundle" ? "Bundles" : "Qty";
    const a = cfg.actual;
    const q = cfg.quote;
    const sheets = bySheet && q?.sheets_needed ? `${q.sheets_needed} ${q.sheets_needed === 1 ? "sheet" : "sheets"}` : null;
    const sheetSize = product.sheet_width_in && product.sheet_height_in ? sizeLabel(product.sheet_width_in, product.sheet_height_in) : null;

    return (
        <PanelField label="Size" theme={theme} testid="field-size" panelTestid="size-popover"
            value={<>
                {sizeLabel(cfg.width, cfg.height)}<span className="mx-1.5 text-zinc-300">·</span>{qtyLabel} {cfg.quantity || 0}
                {sheets && <><span className="mx-1.5 text-zinc-300">·</span><span data-testid="field-size-sheets">{sheets}</span></>}
            </>}
            dot={cfg.sizeChanged && <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-amber-500 align-middle" data-testid="size-changed-dot" />}>
            {presets.length > 0 && (
                <div className="flex flex-wrap gap-1.5 p-1" data-testid="preset-picker">
                    {presets.map((p) => (
                        <button key={p.id} type="button" onClick={() => cfg.selectPreset(p.id)} data-testid={`preset-${p.id}`}
                            className="rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors hover:bg-zinc-100"
                            style={cfg.presetId === p.id ? { borderColor: theme?.primary, background: theme?.primary, color: theme?.on } : { borderColor: "#e4e4e7", color: "#27272a" }}>
                            {p.label}
                            {p.flat_price ? <span className="ml-1.5 font-mono opacity-70">{money(p.flat_price)}</span> : null}
                            {p.per_sheet_count ? <span className="ml-1.5 font-mono opacity-70" data-testid={`preset-yield-${p.id}`}>{p.per_sheet_count}/sheet</span> : null}
                        </button>
                    ))}
                </div>
            )}
            {bySheet && q?.per_sheet_count > 0 && (
                <p className="mx-1 mb-1 rounded-lg bg-zinc-50 px-2.5 py-1.5 text-[11px] leading-snug text-zinc-700" data-testid="sheet-yield-note">
                    {sheetSize ? <>One sheet is <b>{sheetSize}</b>. </> : null}
                    At {sizeLabel(cfg.width, cfg.height)} you get <b>{q.per_sheet_count} per sheet</b>.
                    {" "}{cfg.quantity || 0} pieces = <b>{sheets}</b> at {money(q.tier_price)} a sheet — <b>{q.pieces_printed} printed</b>.
                </p>
            )}
            <div className="grid grid-cols-3 gap-2 p-1" data-testid="dims-fields">
                {cfg.freeDims ? (
                    <>
                        <Num label="Width (in)" value={cfg.width} onChange={cfg.setWidth} min={product.min_width_in} max={product.max_width_in} theme={theme} testid="input-width" />
                        <Num label="Height (in)" value={cfg.height} onChange={cfg.setHeight} min={product.min_height_in} max={product.max_height_in} theme={theme} testid="input-height" />
                    </>
                ) : (
                    <div className="col-span-2">
                        <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">Size</span>
                        <div className="mt-1 rounded-lg border border-zinc-200 bg-zinc-50 px-2.5 py-2 font-mono text-sm text-zinc-700">{sizeLabel(cfg.width, cfg.height)}</div>
                    </div>
                )}
                <Num label={qtyLabel} value={cfg.quantity} onChange={cfg.setQuantity} min={1} theme={theme} testid="input-quantity" />
            </div>
            {a && (
                <p className={`px-2 pb-1.5 pt-0.5 text-[11px] leading-snug ${cfg.sizeChanged ? "text-amber-700" : "text-zinc-500"}`} data-testid="actual-size-note">
                    Actual size of your file: <b>{sizeLabel(a.w, a.h)}</b> ({a.px[0]} × {a.px[1]} px at {a.dpi} dpi{a.embedded ? "" : ", assumed"}).
                    {cfg.sizeChanged && (
                        <button type="button" onClick={cfg.useActualSize} className="ml-1 font-bold underline" data-testid="use-actual-size">Use it</button>
                    )}
                </p>
            )}
            {cfg.freeDims && (
                <p className="px-2 pb-1 text-[11px] text-zinc-400">
                    {product.min_width_in}" to {product.max_width_in}" wide · {product.min_height_in}" to {product.max_height_in}" tall
                </p>
            )}
        </PanelField>
    );
}
